/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SpeedDialFormFields.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form fields component for Speed Dial form.
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { Phone } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { FormBox } from '../../../components/ui/FormBox';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import SelectClassOfService from '../../../components/ui/SelectClassOfService';
import type { SpeedDialFormState } from '../hooks/useSpeedDialData';
import type { FormErrors } from '../hooks/useSpeedDialForm';

interface SpeedDialFormFieldsProps {
  formData: SpeedDialFormState;
  errors: FormErrors;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInputChange: (field: keyof SpeedDialFormState, value: any) => void;
  classOfServices: Array<{ id: number; name: string }>;
  loadingClassOfServices: boolean;
}

export default function SpeedDialFormFields({
  formData,
  errors,
  onInputChange,
  classOfServices,
  loadingClassOfServices
}: SpeedDialFormFieldsProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('speedDials.sections.generalInfo')} icon={<Phone className="h-5 w-5" />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
        {/* Row 1: Speed Code | Input | Destination | Input */}
        <SimpleTooltip 
          content={t('speedDials.fields.speedCode.tooltip')}
          title={t('speedDials.fields.speedCode.label')}
          position="top"
          maxWidth="300px"
        >
          <Label htmlFor="speedCode" required className="text-left">
            {t('speedDials.fields.speedCode.label')}
          </Label>
        </SimpleTooltip>
        <Input
          id="speedCode"
          value={formData.speedCode}
          onChange={(e) => onInputChange('speedCode', e.target.value)}
          placeholder={t('speedDials.fields.speedCode.placeholder')}
          error={errors.speedCode}
          maxLength={32}
        />
        <SimpleTooltip 
          content={t('speedDials.fields.destination.tooltip')}
          title={t('speedDials.fields.destination.label')}
          position="top"
          maxWidth="300px"
        >
          <Label htmlFor="destination" required className="text-left">
            {t('speedDials.fields.destination.label')}
          </Label>
        </SimpleTooltip>
        <Input
          id="destination"
          value={formData.destination}
          onChange={(e) => onInputChange('destination', e.target.value)}
          placeholder={t('speedDials.fields.destination.placeholder')}
          error={errors.destination}
          maxLength={255}
        />

        {/* Row 2: Description | Input | Class of Service | Select */}
        <SimpleTooltip 
          content={t('speedDials.fields.description.tooltip')}
          title={t('speedDials.fields.description.label')}
          position="top"
          maxWidth="300px"
        >
          <Label htmlFor="description" className="text-left">
            {t('speedDials.fields.description.label')}
          </Label>
        </SimpleTooltip>
        <Input
          id="description"
          value={formData.description}
          onChange={(e) => onInputChange('description', e.target.value)}
          placeholder={t('speedDials.fields.description.placeholder')}
          maxLength={255}
        />
        <SimpleTooltip 
          content={t('speedDials.fields.classOfService.tooltip')}
          title={t('speedDials.fields.classOfService.label')}
          position="top"
          maxWidth="300px"
        >
          <Label htmlFor="classOfServicesId" className="text-left">
            {t('speedDials.fields.classOfService.label')}
          </Label>
        </SimpleTooltip>
        <SelectClassOfService
          value={formData.classOfServicesId}
          onChange={(value) => onInputChange('classOfServicesId', value)}
          classOfServices={classOfServices}
          placeholder={t('speedDials.fields.classOfService.placeholder')}
          disabled={loadingClassOfServices}
          error={!!errors.classOfServicesId}
        />

        {/* Row 3: Enabled | Toggle */}
        <SimpleTooltip 
          content={t('speedDials.fields.enabled.tooltip')}
          title={t('speedDials.fields.enabled.label')}
          position="top"
          maxWidth="300px"
        >
          <Label htmlFor="enabled" className="text-left">
            {t('speedDials.fields.enabled.label')}
          </Label>
        </SimpleTooltip>
        <div>
          <Toggle
            id="enabled"
            value={formData.enabled}
            onChange={(checked) => onInputChange('enabled', checked)}
          />
        </div>
      </div>
    </FormBox>
  );
}

